"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useCart } from "../CartContext";
import LanguageSwitcher from "../LanguageSwitcher";
import CurrencySwitcher from "../CurrencySwitcher";
import SearchOverlay from "../SearchOverlay";
import { STORE_ENABLED } from "@/lib/store-config";

interface BrandNavProps {
  locale: string;
  dict: Record<string, unknown>;
}

export default function BrandNav({ locale, dict }: BrandNavProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, openCart } = useCart();

  const nav = (dict.nav as Record<string, string>) ?? {};
  const ui = (dict.ui as Record<string, string>) ?? {};
  const links = [
    // Shop is hidden in showcase mode (sales routed to Nubia).
    ...(STORE_ENABLED ? [{ href: `/${locale}/shop`, label: nav.shop ?? "Shop" }] : []),
    { href: `/${locale}/products`, label: nav.products ?? "Products" },
    { href: `/${locale}/routines`, label: nav.routines ?? "Routines" },
    { href: `/${locale}/quiz`, label: nav.quiz ?? "Skin Quiz" },
    { href: `/${locale}/ingredients`, label: nav.ingredients ?? "Ingredients" },
    { href: `/${locale}/blog`, label: nav.blog ?? "Blog" },
  ];

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "color-mix(in oklab, var(--bp-bg) 92%, transparent)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderBottom: "1px solid var(--bp-rule)",
        }}
      >
        {/* Utility row — desktop only */}
        <div
          className="hidden md:flex"
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px var(--bp-edge)",
          }}
        >
          <div className="bp-mono-sm">{ui.tagline ?? "FORMULATED IN FREIBURG · DISPENSED FROM ISTANBUL"}</div>
          <div className="bp-mono-sm" style={{ display: "flex", gap: 24, alignItems: "center" }}>
            <span>{ui.complianceClaim ?? "100% EU COMPLIANT · 15+ COUNTRIES"}</span>
            {STORE_ENABLED && <CurrencySwitcher />}
            <LanguageSwitcher locale={locale} />
          </div>
        </div>
        <hr className="bp-rule hidden md:block" />

        {/* Main row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            padding: "14px var(--bp-edge)",
            gap: 24,
          }}
        >
          {/* Logo (left) */}
          <Link
            href={`/${locale}`}
            style={{ display: "inline-flex", alignItems: "center", color: "var(--bp-ink)", justifySelf: "start" }}
            aria-label="Beauty & Pharma Dermaceuticals Research Labs"
          >
            <img src="/logo.svg" alt="" style={{ height: 80, width: "auto", display: "block" }} />
          </Link>

          {/* Centered nav (desktop) */}
          <nav
            className="hidden md:flex"
            style={{ gap: 36, justifyContent: "center" }}
          >
            {links.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                style={{
                  fontFamily: "var(--bp-mono)",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--bp-ink)",
                }}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right: search / account / bag */}
          <div style={{ display: "flex", gap: 24, justifyContent: "flex-end", alignItems: "center" }}>
            <button
              className="bp-mono-sm hidden md:inline"
              style={{ color: "var(--bp-ink)" }}
              onClick={() => setSearchOpen(true)}
              aria-label="Search (Ctrl+K)"
            >
              {ui.searchBtn ?? "SEARCH"}
            </button>
            <Link href={`/${locale}/about`} className="bp-mono-sm hidden md:inline" style={{ color: "var(--bp-ink)" }}>
              {ui.aboutBtn ?? "ABOUT"}
            </Link>
            {STORE_ENABLED && (
              <button
                className="bp-mono-sm"
                style={{ color: "var(--bp-ink)", position: "relative" }}
                onClick={openCart}
                aria-label={`Open bag${count > 0 ? `, ${count} items` : ""}`}
              >
                {ui.bag ?? "BAG"} ({String(count).padStart(2, "0")})
              </button>
            )}
            {/* Mobile burger */}
            <button
              className="md:hidden bp-mono-sm"
              style={{ color: "var(--bp-ink)" }}
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (ui.closeX ?? "CLOSE ×") : (ui.menuBtn ?? "MENU")}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div
            className="md:hidden"
            style={{ borderTop: "1px solid var(--bp-rule)", padding: "16px var(--bp-edge) 24px" }}
          >
            <nav style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {links.map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: "var(--bp-serif)",
                    fontWeight: 300,
                    fontSize: 24,
                    letterSpacing: "-0.01em",
                    color: "var(--bp-ink)",
                  }}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <hr className="bp-rule" style={{ margin: "20px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
              {STORE_ENABLED && <CurrencySwitcher />}
              <LanguageSwitcher locale={locale} />
            </div>
          </div>
        )}
      </header>

      <SearchOverlay
        locale={locale}
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        ui={{
          typeToBegin: ui.searchTypeToBegin,
          noResults: ui.searchNoResults,
          escClose: ui.searchEscClose,
          placeholder: ui.searchPlaceholder,
          subtitle: ui.searchSubtitle,
          hints: ui.searchHints,
          nothingFor: ui.searchNothingFor,
          trySuggestion: ui.searchTrySuggestion,
          browse: ui.searchBrowse,
          results: ui.searchResults,
          navigate: ui.searchNavigate,
          openLabel: ui.searchOpenLabel,
        }}
      />
    </>
  );
}
